import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Bike, Users, AlertTriangle, Wrench, Shield, TrendingUp, Battery, ArrowRightLeft, CarFront, FileWarning, UserPlus, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CriticalAlerts from '../components/CriticalAlerts';
import { DirhamIcon, formatAmount } from '../components/DirhamSymbol';
import useDeviceDetection from '../hooks/useDeviceDetection';

// Helper function to format numbers without decimals
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Math.round(num).toLocaleString();
};

// Helper to get month name
const getMonthName = (monthNum) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[monthNum - 1] || '';
};

const Dashboard = ({ user }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStat, setExpandedStat] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // If stats failed to load, show error state
  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="dashboard-error">
        <div className="text-center">
          <p className="text-red-500 mb-2">Failed to load dashboard data</p>
          <button 
            onClick={fetchStats}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('dashboard.totalMotorcycles'),
      value: formatNumber(stats?.total_bikes),
      icon: Bike,
      color: 'from-lime-500 to-green-600',
      testId: 'stat-total-bikes',
      onClick: () => navigate('/motorcycles')
    },
    {
      title: t('dashboard.availableBikes'),
      value: formatNumber(stats?.available_bikes),
      icon: Bike,
      color: 'from-cyan-500 to-blue-500',
      testId: 'stat-available-bikes',
      onClick: () => navigate('/motorcycles?filter=available')
    },
    {
      title: t('dashboard.leasedMotorcycles'),
      value: formatNumber(stats?.leased_bikes),
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-500',
      testId: 'stat-leased-bikes',
      onClick: () => navigate('/motorcycles?filter=leased')
    },
    {
      title: 'Motorcycles Under Maintenance',
      value: formatNumber(stats?.bikes_under_maintenance),
      icon: Wrench,
      color: 'from-yellow-500 to-amber-500',
      testId: 'stat-bikes-maintenance',
      onClick: () => navigate('/maintenance')
    },
    {
      title: t('dashboard.totalRiders'),
      value: formatNumber(stats?.active_riders),
      icon: Users,
      color: 'from-purple-500 to-pink-500',
      testId: 'stat-active-riders',
      onClick: () => navigate('/riders')
    },
    {
      title: 'Insurance Expiring (30 days)',
      value: formatNumber(stats?.insurance_expiring_soon),
      icon: Shield,
      color: 'from-rose-500 to-pink-500',
      testId: 'stat-insurance-expiring',
      onClick: () => navigate('/insurance')
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('dashboard.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {/* Critical Alerts Banner (includes replacement notifications) */}
      <CriticalAlerts user={user} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card 
              key={card.title} 
              className="modern-card stat-card border-0 cursor-pointer" 
              data-testid={card.testId}
              onClick={card.onClick}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  {card.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-4xl font-bold text-lime-600">
                      {card.isCurrency && <DirhamIcon size="0.9em" />}
                      {card.value}
                    </div>
                    {card.subValue && (
                      <div className="text-xs text-slate-500 mt-1">{card.subValue}</div>
                    )}
                  </div>
                  <div className={`p-4 rounded-2xl bg-gradient-to-br ${card.color} shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Battery Swaps */}
            <div 
              className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => setExpandedStat(expandedStat === 'swaps' ? null : 'swaps')}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Battery className="w-4 h-4 text-green-600" />
                  <span className="text-slate-600">Battery Swaps</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-green-600">{formatNumber(stats?.battery_swaps?.total)}</span>
                  {expandedStat === 'swaps' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedStat === 'swaps' && stats?.battery_swaps?.monthly?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  {stats.battery_swaps.monthly.slice(0, 6).map((m, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-slate-500">
                      <span>{getMonthName(m.month)} {m.year}</span>
                      <span className="font-medium">{m.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Motorcycle Handovers */}
            <div 
              className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => setExpandedStat(expandedStat === 'handovers' ? null : 'handovers')}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-blue-600" />
                  <span className="text-slate-600">Motorcycle Handovers</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-blue-600">{formatNumber(stats?.handovers?.total)}</span>
                  {expandedStat === 'handovers' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedStat === 'handovers' && stats?.handovers?.monthly?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  {stats.handovers.monthly.slice(0, 6).map((m, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-slate-500">
                      <span>{getMonthName(m.month)} {m.year}</span>
                      <span className="font-medium">{m.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Motorcycle Takeovers */}
            <div 
              className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => setExpandedStat(expandedStat === 'takeovers' ? null : 'takeovers')}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <CarFront className="w-4 h-4 text-amber-600" />
                  <span className="text-slate-600">Motorcycle Takeovers</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-amber-600">{formatNumber(stats?.takeovers?.total)}</span>
                  {expandedStat === 'takeovers' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedStat === 'takeovers' && stats?.takeovers?.monthly?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  {stats.takeovers.monthly.slice(0, 6).map((m, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-slate-500">
                      <span>{getMonthName(m.month)} {m.year}</span>
                      <span className="font-medium">{m.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Accidents Reported */}
            <div 
              className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => setExpandedStat(expandedStat === 'accidents' ? null : 'accidents')}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <FileWarning className="w-4 h-4 text-red-600" />
                  <span className="text-slate-600">Accidents Reported</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-600">{formatNumber(stats?.accidents?.total)}</span>
                  {expandedStat === 'accidents' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedStat === 'accidents' && stats?.accidents?.monthly?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  {stats.accidents.monthly.slice(0, 6).map((m, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-slate-500">
                      <span>{getMonthName(m.month)} {m.year}</span>
                      <span className="font-medium">{m.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* TAP Candidates */}
            <div 
              className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => setExpandedStat(expandedStat === 'tap' ? null : 'tap')}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-purple-600" />
                  <span className="text-slate-600">TAP Candidates</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-purple-600">{formatNumber(stats?.tap_candidates?.total)}</span>
                  {expandedStat === 'tap' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedStat === 'tap' && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Total</span>
                    <span className="font-medium">{formatNumber(stats?.tap_candidates?.total)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>In Process</span>
                    <span className="font-medium text-amber-600">{formatNumber(stats?.tap_candidates?.in_process)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Ready</span>
                    <span className="font-medium text-green-600">{formatNumber(stats?.tap_candidates?.ready)}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Fleet Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Leased vs Available</span>
                  <span className="font-medium text-slate-800">
                    {formatNumber(stats?.leased_bikes)} / {formatNumber(stats?.total_bikes)}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-lime-500 to-green-600 h-3 rounded-full transition-all"
                    style={{
                      width: `${stats?.total_bikes ? Math.round((stats.leased_bikes / stats.total_bikes) * 100) : 0}%`
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;